/**
 * El explorador del simulador (spike de I-18): una pregunta libre sobre la
 * fuente, que un agente contesta consultando un Bridge de solo lectura.
 *
 * Contrato entre la API (`POST /studio/simulador/explorar`) y el Studio.
 * Como el resto del simulador: la dirección y el token del Bridge viajan
 * en el cuerpo, no se guardan ni vuelven en la respuesta, y nada se
 * persiste. La respuesta trae las cuatro vistas de la pantalla: la
 * petición, el camino (cada herramienta con su resultado), el resultado
 * y lo que costó.
 */
import { z } from "zod";
import { BRIDGE_ALCANCES, BridgeCelda } from "./bridge.js";

export const EXPLORADOR_PREGUNTA_MAX = 4000;
export const EXPLORADOR_FORMATO_MAX = 2000;
/** Filas de cada consulta que vuelven a la pantalla (el modelo puede ver más). */
export const EXPLORADOR_FILAS_VISTA = 50;

export const ExplorarPeticion = z.object({
  bridge: z.object({
    /** `https://<algo>.trycloudflare.com`, o la que la API admita (EXPLORADOR_BRIDGE_HOSTS). */
    url: z.string().url().max(500),
    token: z.string().min(32).max(200),
  }),
  alcance: z.enum(BRIDGE_ALCANCES),
  /** Lo que pediría el asesor: la métrica exacta, con periodo y desglose. */
  pregunta: z.string().trim().min(1).max(EXPLORADOR_PREGUNTA_MAX),
  /** Cómo quiere la respuesta (una tabla con tales columnas, una cifra, un JSON…). */
  formato: z.string().trim().max(EXPLORADOR_FORMATO_MAX).optional(),
});

export const EXPLORADOR_HERRAMIENTAS = ["listar", "describir", "consultar"] as const;

export const PasoExplorador = z.object({
  n: z.number().int().positive(),
  herramienta: z.enum(EXPLORADOR_HERRAMIENTAS),
  /** Lo que el agente escribió antes de pedir la herramienta: su porqué. */
  nota: z.string().optional(),
  /** La entrada tal cual la pidió el modelo (el SQL, el objeto, la búsqueda). */
  entrada: z.record(z.unknown()),
  ok: z.boolean(),
  /** Una línea: «12 objetos», «340 filas en 23 ms», o el error. */
  resumen: z.string(),
  columnas: z.array(z.string()).optional(),
  /** Las primeras filas (EXPLORADOR_FILAS_VISTA como mucho). */
  filas: z.array(z.array(BridgeCelda)).optional(),
  filasTotal: z.number().int().nonnegative().optional(),
  truncado: z.boolean().optional(),
  ms: z.number().nonnegative(),
});

export const ConsumoExplorador = z.object({
  modelo: z.string(),
  vueltas: z.number().int().nonnegative(),
  consultas: z.number().int().nonnegative(),
  tokensEntrada: z.number().int().nonnegative(),
  tokensSalida: z.number().int().nonnegative(),
  tokensCacheLeidos: z.number().int().nonnegative(),
  tokensCacheEscritos: z.number().int().nonnegative(),
  /** Con los precios de la API (model-cost); null si el modelo no tiene precio. */
  importe: z.number().nullable(),
  moneda: z.string().nullable(),
  ms: z.number().nonnegative(),
});

export const EXPLORADOR_FINALES = ["respondida", "agotada", "fallida"] as const;

export const ExplorarRespuesta = z.object({
  peticion: z.object({ pregunta: z.string(), formato: z.string().nullable(), alcance: z.enum(BRIDGE_ALCANCES), baseDeDatos: z.string().nullable() }),
  camino: z.array(PasoExplorador),
  /**
   * `respondida`: el agente cerró con su respuesta. `agotada`: se acabó el
   * presupuesto de vueltas o de tiempo y se le pidió cerrar con lo que
   * tuviera. `fallida`: no se pudo seguir (Bridge caído, modelo).
   */
  final: z.enum(EXPLORADOR_FINALES),
  /** La respuesta del agente, en markdown, con su resultado, cómo lo calculó y sus supuestos. */
  respuesta: z.string().nullable(),
  fallo: z.object({ codigo: z.string(), mensaje: z.string() }).optional(),
  consumo: ConsumoExplorador,
});

export type ExplorarPeticion = z.infer<typeof ExplorarPeticion>;
export type PasoExplorador = z.infer<typeof PasoExplorador>;
export type ConsumoExplorador = z.infer<typeof ConsumoExplorador>;
export type ExplorarRespuesta = z.infer<typeof ExplorarRespuesta>;
